import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { invalidateSafety } from '../lib/safety-invalidation';
import { formatCalendarDate, formatInstant, formatInstantDay } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { getIncidentTypeLabel } from '../lib/incident-types';
import { toast } from 'sonner';
import {
  X, Download, Loader2, AlertTriangle, Clock, MapPin,
  Shield, Users, CheckCircle, FileWarning, Calendar,
} from 'lucide-react';

const STATUS_COLORS: Record<string, string> = {
  reported: 'bg-blue-500/20 text-blue-400',
  investigating: 'bg-amber-500/20 text-amber-400',
  action_required: 'bg-red-500/20 text-red-400',
  closed: 'bg-green-500/20 text-green-400',
  open: 'bg-amber-500/20 text-amber-400',
};

const SEVERITY_COLORS: Record<string, string> = {
  low: 'bg-gray-500/20 text-gray-400',
  medium: 'bg-amber-500/20 text-amber-400',
  high: 'bg-orange-500/20 text-orange-400',
  critical: 'bg-red-500/20 text-red-400',
};

/**
 * The incident type vocabulary lives in `lib/incident-types`. This file used to
 * carry its own copy of the map, six entries against the shared seven, and the
 * missing one was `motor_vehicle` — the type the modal exists to show, since a
 * vehicle incident is the one that carries a repair cost and a preventability
 * call. It fell to the `replace(/_/g, ' ')` fallback and rendered the words in
 * all small letters, beside every other type's Title Case.
 *
 * A local copy of a shared map cannot be kept in step, because nothing fails
 * when it drifts: the fallback is a plausible-looking string. The shared map is
 * `Record<IncidentType, string>` rather than Partial precisely so the next added
 * type breaks the build instead of quietly rendering raw.
 */

/** An instant (regulatory_reported_at), shown as the TENANT's day. */
function fmtDate(d: string | null | undefined, timeZone: string | null) {
  return formatInstantDay(d, timeZone, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';
}

/** incident_date and followup_due_date are calendar days, not instants. */
function fmtDay(d: string | null | undefined) {
  return formatCalendarDate(d, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';
}

/** An instant (reported_at, updated_at), on the TENANT's clock. */
function fmtDateTime(d: string | null | undefined, timeZone: string | null) {
  return formatInstant(d, timeZone, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  }) ?? '—';
}

interface IncidentDetailModalProps {
  incidentId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function IncidentDetailModal({ incidentId, isOpen, onClose }: IncidentDetailModalProps) {
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [rootCause, setRootCause] = useState('');
  const [preventiveActions, setPreventiveActions] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  const { data: incRaw, isLoading, isError } = useQuery({
    queryKey: ['safety-incident-detail', incidentId],
    queryFn: () => api.safety.getIncident(incidentId!).then(r => r.data.data ?? r.data ?? null),
    enabled: isOpen && !!incidentId,
  });

  const inc: any = incRaw;

  // Reset form state when modal opens with new incident
  useEffect(() => {
    if (isOpen && inc) {
      setShowCloseForm(false);
      setRootCause(inc.rootCause || inc.root_cause || '');
      setPreventiveActions(inc.preventiveActions || inc.preventive_actions || '');
    }
  }, [isOpen, inc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !incidentId) return null;

  if (isError) {
    toast.error('Failed to load incident details');
    onClose();
    return null;
  }

  const status = inc?.status || 'reported';
  const incidentType = inc?.incidentType || inc?.incident_type || '';
  const severity = inc?.severityLevel || inc?.severity_level || inc?.severity || 'low';
  const incidentNumber = inc?.incidentNumber || inc?.incident_number || '';
  const title = inc?.title || incidentNumber || 'Untitled';
  const isNearMiss = incidentType === 'near_miss';
  // An absent type is an absent type: no raw value, no guess, an em dash.
  const typeLabel = incidentType ? getIncidentTypeLabel(incidentType) : '—';
  const description = inc?.description;
  const immediateActions = inc?.immediateActions || inc?.immediate_actions;
  const currentRootCause = inc?.rootCause || inc?.root_cause || '';
  const currentPreventiveActions = inc?.preventiveActions || inc?.preventive_actions || '';
  const location = inc?.location;
  const incidentDate = inc?.incidentDate || inc?.incident_date;
  const incidentTime = inc?.incidentTime || inc?.incident_time;
  const reportedBy = inc?.reportedByName || inc?.reported_by_name ||
    [inc?.reporter?.firstName || inc?.reporter?.first_name, inc?.reporter?.lastName || inc?.reporter?.last_name].filter(Boolean).join(' ');
  const reportedAt = inc?.reportedAt || inc?.reported_at;
  const peopleInvolved: any[] = inc?.peopleInvolved || inc?.people_involved || [];
  const photosUrls: string[] = inc?.photosUrls || inc?.photos_urls || [];
  const followupRequired = inc?.followupRequired ?? inc?.followup_required ?? false;
  const followupDueDate = inc?.followupDueDate || inc?.followup_due_date;
  const regulatoryReportable = inc?.regulatoryReportable ?? inc?.regulatory_reportable ?? false;
  const regulatoryReportedAt = inc?.regulatoryReportedAt || inc?.regulatory_reported_at;
  const workersCompClaim = inc?.workersCompClaim ?? inc?.workers_comp_claim ?? false;
  const updatedAt = inc?.updatedAt || inc?.updated_at;

  const needsRootCause = !currentRootCause;
  const needsPreventive = !currentPreventiveActions;
  const needsCloseForm = needsRootCause || needsPreventive;

  const isActioning = !!actionLoading;

  const handleBeginInvestigation = async () => {
    setActionLoading('investigate');
    try {
      await api.safety.updateIncident(incidentId, { status: 'investigating' });
      toast.success('Investigation started');
      invalidateSafety(queryClient);
      queryClient.invalidateQueries({ queryKey: ['safety-incident-detail', incidentId] });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Failed to update incident');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCloseIncident = async () => {
    if (needsCloseForm && !showCloseForm) {
      setShowCloseForm(true);
      return;
    }
    if (showCloseForm) {
      if (needsRootCause && !rootCause.trim()) return;
      if (needsPreventive && !preventiveActions.trim()) return;
    }

    setActionLoading('close');
    try {
      const payload: any = { status: 'closed' };
      if (needsRootCause) payload.rootCause = rootCause.trim();
      if (needsPreventive) payload.preventiveActions = preventiveActions.trim();
      await api.safety.updateIncident(incidentId, payload);
      toast.success('Incident closed');
      invalidateSafety(queryClient);
      queryClient.invalidateQueries({ queryKey: ['safety-incident-detail', incidentId] });
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Failed to close incident');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.safety.getIncidentPdf(incidentId);
      if (res?.data) {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `incident-${incidentId.slice(0, 8)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto p-4 pt-8 pb-8" onClick={onClose}>
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
          </div>
        )}

        {!isLoading && inc && (
          <>
            {/* Header */}
            <div className="p-6 pb-4 border-b border-fieldiq-border flex-shrink-0">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold text-white">{title}</h2>
                  {incidentNumber && incidentNumber !== title && (
                    <p className="text-xs text-fieldiq-muted font-mono mt-0.5">{incidentNumber}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${STATUS_COLORS[status] || 'bg-gray-500/20 text-gray-400'}`}>
                      {status.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${SEVERITY_COLORS[severity] || 'bg-gray-500/20 text-gray-400'}`}>
                      {severity}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize bg-purple-500/20 text-purple-400">
                      {isNearMiss ? 'Near Miss' : typeLabel}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {incidentDate && (
                      <p className="text-xs text-fieldiq-text flex items-center gap-1">
                        <Calendar size={11} className="text-fieldiq-muted" />
                        {fmtDay(incidentDate)}{incidentTime ? ` at ${incidentTime}` : ''}
                      </p>
                    )}
                    {location && (
                      <p className="text-xs text-fieldiq-text flex items-center gap-1">
                        <MapPin size={11} className="text-fieldiq-muted" />
                        {location}
                      </p>
                    )}
                    {reportedBy && (
                      <p className="text-xs text-fieldiq-text flex items-center gap-1">
                        <Shield size={11} className="text-fieldiq-muted" />
                        Reported by {reportedBy}{reportedAt ? ` on ${fmtDateTime(reportedAt, timeZone)}` : ''}
                      </p>
                    )}
                  </div>
                </div>
                <button onClick={onClose} className="text-fieldiq-muted hover:text-white transition-colors flex-shrink-0">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Body — scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">

              {/* Description */}
              {description && (
                <div>
                  <h3 className="text-sm font-bold text-white mb-2">Description</h3>
                  <div className="bg-fieldiq-dark rounded-lg p-4">
                    <p className="text-sm text-fieldiq-text whitespace-pre-wrap">{description}</p>
                  </div>
                </div>
              )}

              {/* Immediate Actions */}
              {immediateActions && (
                <div>
                  <h3 className="text-sm font-bold text-white mb-2">Immediate Actions</h3>
                  <div className="bg-fieldiq-dark rounded-lg p-4">
                    <p className="text-sm text-fieldiq-text whitespace-pre-wrap">{immediateActions}</p>
                  </div>
                </div>
              )}

              {/* People Involved */}
              {peopleInvolved.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-1">
                    <Users size={13} className="text-fieldiq-muted" /> People Involved
                  </h3>
                  <div className="bg-fieldiq-dark rounded-lg p-4 space-y-2">
                    {peopleInvolved.map((p: any, i: number) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-sm text-white">{p.name || p.firstName || `Person ${i + 1}`}</span>
                        {(p.role || p.title) && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-fieldiq-border text-fieldiq-muted">{p.role || p.title}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Photos */}
              {photosUrls.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-white mb-2">Photos</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {photosUrls.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                        <img src={url} alt={`Incident photo ${i + 1}`} className="w-full h-20 object-cover rounded-lg border border-fieldiq-border hover:border-fieldiq-accent transition-colors" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Root Cause */}
              {currentRootCause && (
                <div>
                  <h3 className="text-sm font-bold text-white mb-2">Root Cause</h3>
                  <div className="bg-fieldiq-dark rounded-lg p-4">
                    <p className="text-sm text-fieldiq-text whitespace-pre-wrap">{currentRootCause}</p>
                  </div>
                </div>
              )}

              {/* Preventive Actions */}
              {currentPreventiveActions && (
                <div>
                  <h3 className="text-sm font-bold text-white mb-2">Preventive Actions</h3>
                  <div className="bg-fieldiq-dark rounded-lg p-4">
                    <p className="text-sm text-fieldiq-text whitespace-pre-wrap">{currentPreventiveActions}</p>
                  </div>
                </div>
              )}

              {/* Followup */}
              {followupRequired && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                  <p className="text-sm text-amber-400 flex items-center gap-2">
                    <FileWarning size={14} />
                    Followup required{followupDueDate ? ` by ${fmtDay(followupDueDate)}` : ''}
                  </p>
                </div>
              )}

              {/* Regulatory / Workers Comp */}
              {(regulatoryReportable || workersCompClaim) && (
                <div className="bg-fieldiq-dark rounded-lg p-4 space-y-2">
                  {regulatoryReportable && (
                    <p className="text-sm text-fieldiq-text flex items-center gap-2">
                      <AlertTriangle size={13} className="text-red-400" />
                      Regulatory reportable{regulatoryReportedAt ? ` — reported ${fmtDate(regulatoryReportedAt, timeZone)}` : ' — not yet reported'}
                    </p>
                  )}
                  {workersCompClaim && (
                    <p className="text-sm text-fieldiq-text flex items-center gap-2">
                      <Shield size={13} className="text-amber-400" />
                      Workers' comp claim
                    </p>
                  )}
                </div>
              )}

              {/* Closed status */}
              {status === 'closed' && updatedAt && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                  <p className="text-sm text-green-400 flex items-center gap-2">
                    <CheckCircle size={14} />
                    Closed on {fmtDateTime(updatedAt, timeZone)}
                  </p>
                </div>
              )}

              {/* Inline close form */}
              {showCloseForm && (
                <div className="space-y-3">
                  {needsRootCause && (
                    <div>
                      <label className="text-sm text-fieldiq-text block mb-1">Root Cause *</label>
                      <textarea
                        value={rootCause}
                        onChange={e => setRootCause(e.target.value)}
                        placeholder="What was the root cause of this incident?"
                        rows={3}
                        className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white placeholder-fieldiq-muted focus:border-fieldiq-accent outline-none resize-none"
                        autoFocus
                      />
                    </div>
                  )}
                  {needsPreventive && (
                    <div>
                      <label className="text-sm text-fieldiq-text block mb-1">Preventive Actions *</label>
                      <textarea
                        value={preventiveActions}
                        onChange={e => setPreventiveActions(e.target.value)}
                        placeholder="What actions should be taken to prevent recurrence?"
                        rows={3}
                        className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white placeholder-fieldiq-muted focus:border-fieldiq-accent outline-none resize-none"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer — sticky */}
            <div className="p-4 border-t border-fieldiq-border flex items-center justify-between gap-2 flex-shrink-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  disabled={isActioning}
                  className="px-4 py-2 text-sm text-fieldiq-text hover:text-white rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading || isActioning}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-fieldiq-accent hover:bg-fieldiq-accent/10 rounded-lg transition-colors disabled:opacity-50"
                >
                  {pdfLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  PDF
                </button>
              </div>
              <div className="flex items-center gap-2">
                {status === 'reported' && (
                  <button
                    onClick={handleBeginInvestigation}
                    disabled={isActioning}
                    className="px-4 py-2 text-sm font-medium bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {actionLoading === 'investigate' ? 'Investigating...' : 'Begin Investigation'}
                  </button>
                )}
                {(status === 'investigating' || status === 'action_required') && (
                  <button
                    onClick={handleCloseIncident}
                    disabled={isActioning || (showCloseForm && (
                      (needsRootCause && !rootCause.trim()) || (needsPreventive && !preventiveActions.trim())
                    ))}
                    className="px-4 py-2 text-sm font-medium bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {actionLoading === 'close' ? 'Closing...' : 'Close Incident'}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
